import { useCallback, useState } from 'react';
import { Bar, Card, Chip } from '@/shared/ui/atoms';
import { Prose } from '@/features/onboarding/components/Prose';
import { CHECKLIST } from '@/features/onboarding/data/checklist';

const STORAGE_KEY = 'onboarding.checklist.done';

/**
 * 체크 상태는 이 브라우저에만 남는다 — 사람마다 다른 진행이고 서버에 둘 이유가 없다.
 * 저장이 막히는 환경(사파리 프라이빗 등)에서도 체크 자체는 동작하게 읽기·쓰기 실패를 삼킨다.
 */
function loadDoneIds(): Set<string> {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return new Set<string>(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set<string>();
  }
}

function saveDoneIds(ids: Set<string>): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify([...ids]));
  } catch {
    // 저장 실패가 화면을 막지는 않게 한다.
  }
}

function useDoneIds(): [Set<string>, (id: string) => void, () => void] {
  const [done, setDone] = useState<Set<string>>(loadDoneIds);

  const toggle = useCallback((id: string) => {
    setDone((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      saveDoneIds(next);
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    const empty = new Set<string>();
    saveDoneIds(empty);
    setDone(empty);
  }, []);

  return [done, toggle, reset];
}

export function ChecklistView(): React.JSX.Element {
  const [done, toggle, reset] = useDoneIds();

  const allItems = CHECKLIST.flatMap((group) => group.items);
  const doneCount = allItems.filter((item) => done.has(item.id)).length;
  const percent = Math.round((doneCount / allItems.length) * 100);

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-lg border border-line bg-surface px-4 py-3">
        <div className="flex items-baseline gap-2">
          <strong className="text-[13px]">✅ 온보딩 진행</strong>
          <span className="text-[12px] text-muted">
            {doneCount} / {allItems.length}
          </span>
          <span className="text-2xl font-bold tabular-nums">{percent}%</span>
          <button
            type="button"
            onClick={reset}
            className="ml-auto cursor-pointer text-[11.5px] text-muted hover:text-accent hover:underline"
          >
            초기화
          </button>
        </div>
        <div className="mt-2">
          <Bar percent={percent} />
        </div>
        <p className="mt-2 text-[11px] text-faint">
          체크는 이 브라우저에만 저장됩니다. 권한 항목이 막히면 표시된 담당자에게 바로 요청해 주세요 — 첫 주가 그걸로 갈립니다.
        </p>
      </div>

      {CHECKLIST.map((group) => {
        const groupDone = group.items.filter((item) => done.has(item.id)).length;
        return (
          <Card key={group.key} title={group.title} hint={`${group.when} · ${groupDone}/${group.items.length}`}>
            <ul className="flex flex-col">
              {group.items.map((item) => {
                const checked = done.has(item.id);
                return (
                  <li key={item.id} className="border-b border-line-soft last:border-b-0">
                    <label className="flex cursor-pointer items-baseline gap-2.5 py-2">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(item.id)}
                        className="mt-0.5 size-3.5 accent-accent"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="flex flex-wrap items-baseline gap-2">
                          <strong className={`text-[13px] ${checked ? 'text-faint line-through' : ''}`}>{item.label}</strong>
                          {item.ask && <Chip>요청 · {item.ask}</Chip>}
                        </span>
                        <span className="block text-[12px] text-muted"><Prose text={item.detail} /></span>
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </Card>
        );
      })}
    </div>
  );
}
